import type { ReportArtifact, ReportRenderOptions } from "./work.js";

/**
 * Which stored PDF Entrega hands over for a given format, and whether it must render a new one
 * first (pilot R-56, 2026-11-05).
 *
 * A stored artifact is bytes and never changes (ADR 0010, ADR 0011): a report that was delivered
 * must stay retrievable exactly as delivered. The way out, when the layout has moved on since the
 * PDF was rendered:
 *
 * - `render`   — no PDF for this format yet.
 * - `reuse`    — the newest PDF for this format was rendered by the template in force: use it.
 * - `stale`    — it was rendered by an older template and NOBODY has received it: render again,
 *                the new one supersedes it (the old stays in the list and downloadable).
 * - `frozen`   — it was rendered by an older template and a delivery is recorded: keep serving
 *                it; the laboratory may choose «Volver a emitir» knowingly, which renders a new
 *                one that supersedes it for future downloads.
 *
 * Artifacts already superseded are never candidates: the newest surviving one for the format is.
 */
export type ReportReuse =
  | { action: "render"; artifact: null }
  | { action: "reuse" | "stale" | "frozen"; artifact: ReportArtifact };

export function reportToReuse(artifacts: readonly ReportArtifact[], options: ReportRenderOptions): ReportReuse {
  const candidates = artifacts
    .filter((a) => a.options.letterhead === options.letterhead && a.options.signature === options.signature && a.superseded_by_id === null)
    .sort((a, b) => b.rendered_at.localeCompare(a.rendered_at));
  const newest = candidates[0];
  if (!newest) return { action: "render", artifact: null };
  if (newest.current_template) return { action: "reuse", artifact: newest };
  return { action: newest.delivered_at === null ? "stale" : "frozen", artifact: newest };
}
